import React, { useEffect, useState } from 'react'
import { ActivityIndicator, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { Stack, useMediaQuery } from '@mui/material'
import { Howl } from 'howler'
import { getSnapshot } from 'mobx-state-tree'

import { useStores, setPageTitle, useQuery } from '@trivie/core'
import { Countdown } from '@trivie/ui-mobile'
import { Text } from '@trivie/ui-web/components/text'
import { QuizItemList } from '@trivie/ui-mobile/components/quiz/QuizItemList'
import { Volume } from '@trivie/ui-mobile/components/quiz/Volume'
import { Exit } from '@trivie/ui-mobile/components/quiz/Exit'
import * as Sentry from '@sentry/react'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

const music = new Howl({
  src: ['/game.mp3'],
  loop: true,
  volume: 0.4,
  html5: true,
})

export const GameScreen = observer(() => {
  const { manageUserStore, gameStore, userStore, uiStore, cardStore } = useStores()
  const { setSelectedCard } = cardStore
  const history = useHistory()
  const query = useQuery()
  const isMobile = useMediaQuery('(max-width:800px)')
  const confirm = useConfirm()

  const { branding } = uiStore
  const { startAssessment, startReinforcement, startTutorial, startQuiz, setQuiz, quiz, loading } =
    gameStore
  const { user, getUser, updateUser } = userStore
  const { text_color, quiz_background } = branding
  const { music_enabled } = user!

  const [showCountdown, setShowCountdown] = useState(true)

  const metadata = {
    topic_id: Number(query.get('topic_id')) || null,
    quiz_id: Number(query.get('quiz_id')) || null,
    assignment_id: Number(query.get('assignment_id')) || null,
    subscription_user_id: Number(query.get('subscription_user_id')) || null,
    path_id: Number(query.get('path_id')) || null,
    content_user_id: Number(query.get('content_user_id')) || null,
  }

  const tutorial = useRouteMatch('/game/tutorial')?.path === '/game/tutorial'
  const reinforcement = useRouteMatch('/game/reinforcement')?.path === '/game/reinforcement'
  const assessment = useRouteMatch(`/game/test`)?.path === `/game/test`

  // @ts-ignore
  useEffect(() => {
    setSelectedCard(undefined)
    if (user) {
      getUser(user.id)
    }
    if (tutorial) {
      startTutorial(manageUserStore.isSubscriptionWorkspace)
    } else if (reinforcement) {
      startReinforcement(Number(metadata.topic_id!))
    } else if (assessment) {
      startAssessment(Number(metadata!.quiz_id), metadata)
    } else {
      startQuiz(Number(metadata!.quiz_id), metadata)
    }

    if (music_enabled) {
      music.play()
    }
    return () => music.stop()
  }, [])

  useEffect(() => {
    setPageTitle(quiz?.reinforcement ? 'Reinforcement Quiz' : quiz?.title || '')
  }, [quiz?.title])

  const goToResults = () => {
    if (tutorial) {
      history.replace(`/results/tutorial`, {})
    } else {
      if (metadata.topic_id) {
        history.replace(`/results/${quiz?.result.id}/${metadata.topic_id}`, {
          // @ts-ignore
          from: history.location.state?.from || 'game',
        })
      } else if (quiz?.reinforcement) {
        history.replace(`/results/reinforcement`, {
          // @ts-ignore
          from: history.location.state?.from || 'game',
        })
      } else {
        history.replace(`/results/${quiz?.result.id}`, {
          // @ts-ignore
          from: userStore?.sharedQuiz ? 'share' : history.location.state?.from || 'game',
        })
      }
    }
  }

  const [showingAlert, setShowingAlert] = useState(false)

  useEffect(() => {
    if (quiz?.completed && quiz?.status === 'done') {
      goToResults()
    } else if (quiz?.status === 'error' && !showingAlert) {
      setShowingAlert(true)
      Sentry.captureMessage(`Quiz Error - ${JSON.stringify(getSnapshot(quiz))}`, 'debug')
      confirm({
        title: `Unexpected Error`,
        confirmationText: 'Okay',
        hideCancelButton: true,
        allowClose: false,
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        dialogText:
          'There was an issue submitting your answers. Please try again later or contact your learning administrator if this problem continues to occur.',
      })
        .then(async () => {
          music.stop()
          setShowingAlert(false)
          setQuiz(undefined)
          if (quiz?.reinforcement) {
            history.replace('/play')
          } else {
            history.goBack()
          }
        })
        .catch(() => {})
    }
  }, [quiz?.completed, quiz?.status])

  const exit = () => {
    music.stop()
    quiz?.submitAnswers()
    setQuiz(undefined)
    if (quiz?.reinforcement) {
      history.replace('/play')
    } else {
      history.goBack()
    }
  }

  const toggleMusic = () => {
    if (music_enabled) {
      music.pause()
    } else {
      music.play()
    }
    updateUser({ music_enabled: !music_enabled })
  }

  if (showCountdown) {
    return (
      <Countdown
        style={{ ...CONTAINER, backgroundColor: quiz_background }}
        setVisible={setShowCountdown}
      />
    )
  } else if (!quiz || loading || quiz?.status === 'submitting' || quiz?.status === 'error') {
    return (
      <View style={{ ...CONTAINER, backgroundColor: quiz_background }}>
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <ActivityIndicator />
        </View>
      </View>
    )
  }

  if (!quiz) {
    return null
  }

  const { title } = quiz
  const titleStyle = { fontSize: isMobile ? 18 : 24, color: text_color }

  return (
    <View style={{ ...CONTAINER, backgroundColor: quiz_background }}>
      <Stack gap={2} padding={2} alignItems="center" direction="row" justifyContent="space-between">
        <Exit color={text_color} onPress={exit} />
        <Text noWrap text={title} variant="headline" style={titleStyle} />
        <Volume color={text_color} muted={!music_enabled} onPress={toggleMusic} />
      </Stack>
      <QuizItemList quiz={quiz} branding={branding} />
    </View>
  )
})

const CONTAINER: ViewStyle = {
  position: 'absolute',
  height: '100%',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 998,
}
